import { useState, useRef, useEffect, useMemo, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { ChevronDown } from "lucide-react";
import { DropdownInput, type DropdownOptionType } from "./index";
import { ComboBox, type ComboBoxOptionType } from "./index";
import {
  parseColorValue,
  normalizeColorValue,
} from "./color-picker/utils/colorFormatUtils";
import ColorSwatch from "./ColorSwatch";

interface HexInputProps {
  value: string;
  onChange: (value: string) => void;
  onCommit: (value: string) => void;
  disabled?: boolean;
  dropdownOptions?: DropdownOptionType[];
  onDropdownSelected?: (option: DropdownOptionType) => void;
  comboboxOptions?: ComboBoxOptionType[];
  onComboboxSelected?: (option: ComboBoxOptionType) => void;
  buildArbitraryClass?: (input: string) => ComboBoxOptionType;
  buildOptionPreview?: (option: ComboBoxOptionType) => React.ReactNode;
  onOpenVariantModal?: () => void;
  variantAnchorRef?: React.RefObject<HTMLButtonElement>;
  onBeforeSetValue?: () => void;
  onClear?: () => void;
  placeholder?: string;
  className?: string;
}

const HexInput: React.FC<HexInputProps> = ({
  value,
  onChange,
  onCommit,
  disabled = false,
  dropdownOptions = [],
  onDropdownSelected,
  comboboxOptions,
  onComboboxSelected,
  buildArbitraryClass,
  buildOptionPreview,
  onOpenVariantModal,
  variantAnchorRef,
  onBeforeSetValue,
  onClear,
  placeholder = "#000000",
  className,
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [comboboxOpen, setComboboxOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Update input value when prop value changes
  useEffect(() => {
    // Display the hex representation of the color in the input
    if (value) {
      if (value.startsWith("#")) {
        setInputValue(value);
      } else {
        // For Tailwind classes, show the hex equivalent
        const parsed = parseColorValue(value, comboboxOptions);
        setInputValue(parsed.isValid ? parsed.hex : value);
      }
    } else {
      setInputValue("");
    }
  }, [value, comboboxOptions]);

  // Parse the value to get the proper hex color for the swatch
  const swatchColor = useMemo(() => {
    if (!value) return "#000000";

    // If it's already a hex value, use it directly
    if (value.startsWith("#")) {
      return normalizeColorValue(value);
    }

    // Otherwise, try to parse it as a Tailwind class or other color format
    const parsed = parseColorValue(value, comboboxOptions);
    return parsed.hex;
  }, [value, comboboxOptions]);

  // Helper function to check if two color values represent the same color
  const isSameColor = (value1: string, value2: string): boolean => {
    if (value1 === value2) return true;

    const parsed1 = parseColorValue(value1, comboboxOptions);
    const parsed2 = parseColorValue(value2, comboboxOptions);

    return parsed1.hex === parsed2.hex;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    // Only call onChange if the color value has meaningfully changed
    if (!isSameColor(value, newValue)) {
      onChange(newValue);
    }
  };

  const handleInputCommit = () => {
    // Only commit if the color value has meaningfully changed
    if (!isSameColor(value, inputValue)) {
      onCommit(inputValue);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      handleInputCommit();
      inputRef.current?.blur();
    } else if (event.key === "Escape") {
      setInputValue(value); // Reset to original value
      inputRef.current?.blur();
    }
  };

  const handleDropdownOptionClick = (option: DropdownOptionType) => {
    if (onBeforeSetValue) onBeforeSetValue();
    if (option.value === "clear" && onClear) {
      onClear();
    } else if (onDropdownSelected) {
      onDropdownSelected(option);
    }
    setDropdownVisible(false);
  };

  const handleComboboxOptionClick = (option: ComboBoxOptionType) => {
    if (onBeforeSetValue) onBeforeSetValue();
    if (onComboboxSelected) {
      onComboboxSelected(option);
    }
    setComboboxOpen(false);
    if (inputRef.current) {
      inputRef.current.blur();
    }
  };

  return (
    <div
      className={cn(
        "flex items-center gap-1 w-full h-7 bg-secondary-n-950 rounded-md relative",
        className
      )}
    >
      <div className="ml-1 absolute">
        <ColorSwatch color={swatchColor} size="sm" />
      </div>
      <input
        ref={inputRef}
        disabled={disabled}
        value={inputValue}
        onChange={handleInputChange}
        onFocus={(event) => {
          event.target.select();
          setComboboxOpen(true);
        }}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          handleInputCommit();
        }}
        className={`text-xs pl-5 pr-8 border-none outline-none font-medium w-full h-full rounded-md bg-secondary-n-950 ${
          disabled ? "text-secondary-600" : "text-text-primary"
        }`}
        placeholder={placeholder}
      />
      <button
        type="button"
        onClick={
          onOpenVariantModal
            ? onOpenVariantModal
            : () => setDropdownVisible(true)
        }
        className="absolute right-1"
        ref={variantAnchorRef}
      >
        <ChevronDown className="text-secondary-500 h-4 w-4" />
      </button>
      <DropdownInput
        className="w-40"
        dropdownOptions={dropdownOptions}
        onDropdownSelected={handleDropdownOptionClick}
        visible={Boolean(dropdownVisible)}
        setVisible={setDropdownVisible}
      />
      {comboboxOpen &&
        comboboxOptions &&
        buildArbitraryClass &&
        buildOptionPreview && (
          <ComboBox
            input={inputValue || ""}
            options={comboboxOptions}
            open={comboboxOpen}
            closeCombobox={() => setComboboxOpen(false)}
            onOptionSelected={handleComboboxOptionClick}
            buildOptionPreview={buildOptionPreview}
            buildArbitraryClass={buildArbitraryClass}
            anchorRef={inputRef}
          />
        )}
    </div>
  );
};

export default HexInput;
